import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { FC, useContext, useEffect, useState } from 'react';

import { useBoostLocation } from '@features/boostMyJobs';
import BoostModalTitle from '@features/boostMyJobs/components/BoostModalTitle/BoostModalTitle';
import BoostNote from '@features/boostMyJobs/components/BoostNote/BoostNote';
import { locationBoostOptions } from '@features/boostMyJobs/components/LocationBoostModal/constants';
import LocationBoostContent from '@features/boostMyJobs/components/LocationBoostModalContent/LocationBoostContent';
import { bold, medium } from '@styles/fontWeights';
import { useEnterpriseBoostLocation } from '@features/boostMyJobs/hooks/useEnterpriseBoostLocation';
import { authContext } from '@staffedupusa/shared-components';
import ChargeforBoost from '@features/enterprise/components/ChargeforBoost';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { Close } from '@mui/icons-material';
import { useGetPaymantDetails } from '@features/payments/hooks/useGetPaymantDetails';

type LocationBoostModalProps = {
  isOpen: boolean;
  selectedCompanyId?: string | number;
  companySelfPaid?: boolean;
  handleClose: () => void;
};

const LocationBoostModal: FC<LocationBoostModalProps> = ({
  isOpen,
  selectedCompanyId,
  companySelfPaid,
  handleClose,
}) => {
  const { authData } = useContext(authContext);
  const isEnterprise = authData?.user?.enterprise;
  const isCompany = authData?.user?.company;
  const isCompanyPaid = authData?.user?.company?.is_company_paid;
  const [errorText, setErrorText] = useState('');
  const [numberOfWeeks, setNumberOfWeeks] = useState<number>(2);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [hasSelfPaymentMethod, setHasSelfPaymentMethod] = useState(false);

  const { boostLocation, isBoostLocationLoading } = useBoostLocation({
    setErrorText,
  });
  const { boostEnterpriseLocation, isBoostEnterpriseLocationLoading } =
    useEnterpriseBoostLocation({
      setErrorText,
    });
  const { paymentInfo } = useGetPaymantDetails({
    companyId: selectedCompanyId,
  });
  const { paymentInfo: enterprisePaymentInfo } = useGetPaymantDetails();

  const handleBoost = () => {
    const options = {
      onSuccess: () => {
        toast.success('Boost Successful');
        handleClose();
      },
    };
    if (isCompany) {
      if ((hasSelfPaymentMethod && isCompanyPaid) || !isCompanyPaid) {
        boostLocation({ number_weeks: numberOfWeeks }, options);
      } else {
        setShowPaymentModal(true);
      }
    } else if (
      (isEnterprise && !companySelfPaid && hasSelfPaymentMethod) ||
      (isEnterprise && companySelfPaid && hasPaymentMethod)
    ) {
      boostEnterpriseLocation(
        { number_weeks: numberOfWeeks, company_id: selectedCompanyId },
        options,
      );
    } else if (isEnterprise && companySelfPaid && !hasPaymentMethod) {
      setShowPaymentModal(true);
    }
  };

  useEffect(() => {
    if (
      paymentInfo &&
      paymentInfo?.object &&
      paymentInfo?.object?.data?.length > 0
    ) {
      setHasPaymentMethod(true);
    }
  }, [paymentInfo]);

  useEffect(() => {
    if (
      enterprisePaymentInfo &&
      enterprisePaymentInfo?.object &&
      enterprisePaymentInfo?.object?.data?.length > 0
    ) {
      setHasSelfPaymentMethod(true);
    } else {
      setHasSelfPaymentMethod(false);
    }
  }, [enterprisePaymentInfo]);

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
        <br />
        <BoostModalTitle title="StaffedUp Location Boost!" />
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        <Stack sx={{ marginBottom: 4 }} spacing={4} alignItems="center">
          {errorText && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {errorText}
            </Alert>
          )}
          <LocationBoostContent />
          <Stack spacing={2} direction="row" justifyContent="center">
            {locationBoostOptions.map(({ title, price, value }) => (
              <Button
                key={value}
                variant={numberOfWeeks === value ? 'contained' : 'outlined'}
                sx={{ fontWeight: medium }}
                onClick={() => setNumberOfWeeks(value)}
              >
                {title}
                <Typography
                  component="span"
                  color={
                    numberOfWeeks === value
                      ? 'green.contrastText'
                      : 'green.main'
                  }
                  ml={1}
                  sx={{ fontWeight: bold }}
                >
                  {price}
                </Typography>
              </Button>
            ))}
          </Stack>
          <LoadingButton
            onClick={handleBoost}
            loading={isBoostEnterpriseLocationLoading}
            variant="contained"
            size="large"
            disabled={isBoostLocationLoading}
            sx={{ textTransform: 'uppercase' }}
          >
            Request My Boost now
          </LoadingButton>
          <BoostNote color="text.secondary" />
        </Stack>
      </DialogContent>
      {showPaymentModal && (
        <ChargeforBoost
          handleClose={() => setShowPaymentModal(false)}
          selectedCompanyId={selectedCompanyId}
          numberOfWeeks={numberOfWeeks}
          locationBoost={true}
        />
      )}
    </Dialog>
  );
};

export default LocationBoostModal;
